import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { qrPath } from "../../lib/qr";
import { APP_GET_URL, APP_STORE_URL, PLAY_STORE_URL, type AppStore } from "../../lib/app-store";

export const APP_NAVY = "#110E39";
export const APP_MUTED = "#5a5d77";
export const APP_GOLD = "#E5B265";
export const APP_NAVY_GRADIENT = "linear-gradient(135deg, #2E2878 0%, #1A1454 48%, #110E39 100%)";

/** The app's own icon (the gold one on the home screen). */
export function AppIcon({ size = 48 }: { size?: number }) {
  return (
    <img
      src="/images/app/icon.webp"
      alt=""
      width={size}
      height={size}
      decoding="async"
      className="shrink-0"
      style={{ width: size, height: size, borderRadius: size * 0.24 }}
    />
  );
}

/** QR to `majourneys.com/app`, which sends each phone to its own store. */
export function AppQr({ size = 80 }: { size?: number }) {
  const { t } = useTranslation();
  const q = useMemo(() => qrPath(APP_GET_URL), []);
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${q.size} ${q.size}`}
      role="img"
      aria-label={t("app_promo.qr_label")}
      className="shrink-0 rounded-[10px] bg-white"
    >
      <path d={q.d} fill={APP_NAVY} />
    </svg>
  );
}

/**
 * The two official store badges. `onPick` reports which one was tapped
 * (analytics). `dark` adds a faint ring so the black badges read on navy.
 */
export function StoreBadges({
  height = 44,
  onPick,
}: {
  height?: number;
  onPick?: (store: AppStore) => void;
}) {
  const { t } = useTranslation();
  const badges = [
    { store: "ios" as const, href: APP_STORE_URL, src: "/images/app-store.webp", label: t("app_promo.app_store_label") },
    { store: "android" as const, href: PLAY_STORE_URL, src: "/images/google-play.webp", label: t("app_promo.play_label") },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2">
      {badges.map((b) => (
        <a
          key={b.store}
          href={b.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={b.label}
          onClick={() => onPick?.(b.store)}
          className="mj-press block rounded-[10px]"
        >
          <img
            src={b.src}
            alt=""
            width={Math.round(height * 2.88)}
            height={height}
            loading="lazy"
            decoding="async"
            className="block object-contain"
            style={{ height, width: Math.round(height * 2.88) }}
          />
        </a>
      ))}
    </div>
  );
}
